import {
  IRClockCourseData,
  IRShellMeshData,
  LaserHeatModel,
  LaserScanClockCourseIndexesType,
  LaserScanDataModel,
  LidarZoneSelectionModel,
  QuarterlyMeshDataType,
} from "../types/laserScanScene";
import { CurrentProcessedThermalImageDataModel } from "../../irScanAnalysis/hooks/useGetProcessedThermalImages";
import {
  VISUAL_MAP_BOTTOM_COLORS_SCALE,
  VISUAL_MAP_SIDEWALL_COLORS_SCALE,
} from "../../../constants/heatMapValues";
import { positionSuffixMap } from "../constant/laserScene";

// Worker context tipini tanımlayalım

export enum SceneWorkerActions {
  INITIALIZE = "INITIALIZE",
  CALCULATE_POINT_CLOUD = "CALCULATE_POINT_CLOUD",
  POINT_CLOUD_RESULT = "POINT_CLOUD_RESULT",
  CALCULATE_IR_SHELL = "CALCULATE_IR_SHELL",
  IR_SHELL_RESULT = "IR_SHELL_RESULT",
  CALCULATE_DELAUNAY_MESH = "CALCULATE_DELAUNAY_MESH",
  DELAUNAY_RESULT = "DELAUNAY_RESULT",
}

export interface WorkerInitializePayload {
  bottomColorsScale: typeof VISUAL_MAP_BOTTOM_COLORS_SCALE;
  sidewallColorsScale: typeof VISUAL_MAP_SIDEWALL_COLORS_SCALE;
  quadrantOrder: number[];
  positionSuffixMap: typeof positionSuffixMap;
  colorMap: string[] | [number, number, number, number][];
  infraredMinValue: number;
  infraredMaxValue: number;
  isImperial: boolean;
}

const SceneWorker = () => {
  //#region THREE JS Classes
  interface AttributeMap {
    [name: string]: WorkerBufferAttribute;
  }

  class WorkerBufferGeometry {
    attributes: AttributeMap = {};
    index: number[] | null = null;

    setAttribute(name: string, attribute: WorkerBufferAttribute) {
      this.attributes[name] = attribute;
      return this;
    }

    setIndex(indices: number[]) {
      this.index = indices;
      return this;
    }
  }
  class WorkerColor {
    r: number = 0;
    g: number = 0;
    b: number = 0;

    constructor(color?: string | number) {
      if (color !== undefined) {
        this.set(color);
      }
    }

    set(color: string | number): WorkerColor {
      if (typeof color === "string") {
        const hex = color.replace("#", "");
        this.r = parseInt(hex.substring(0, 2), 16) / 255;
        this.g = parseInt(hex.substring(2, 4), 16) / 255;
        this.b = parseInt(hex.substring(4, 6), 16) / 255;
      }
      return this;
    }

    setRGB(r: number, g: number, b: number): WorkerColor {
      this.r = r;
      this.g = g;
      this.b = b;
      return this;
    }
  }

  class WorkerBufferAttribute {
    constructor(
      public array: Float32Array | Uint8Array,
      public itemSize: number,
    ) {}
  }

  // Worker'a özel BufferGeometry ve BufferAttribute tanımlamaları
  const BufferGeometry = WorkerBufferGeometry;
  const BufferAttribute = WorkerBufferAttribute;
  const Color = WorkerColor;

  //#endregion

  let workerConfig: WorkerInitializePayload;

  const convertInchToUserPreference = (value: number) => {
    return workerConfig.isImperial ? value : value * 25.4;
  };

  const initialize = (payload: WorkerInitializePayload) => {
    workerConfig = {
      bottomColorsScale: payload.bottomColorsScale,
      sidewallColorsScale: payload.sidewallColorsScale,
      colorMap: payload.colorMap,
      infraredMinValue: payload.infraredMinValue,
      quadrantOrder: payload.quadrantOrder,
      positionSuffixMap: payload.positionSuffixMap,
      infraredMaxValue: payload.infraredMaxValue,
      isImperial: payload.isImperial,
    };
  };

  self.onmessage = async (event) => {
    const { type, payload } = event.data;
    let pointCloudResult, irShellResult, delaunayResult;

    switch (type) {
      case SceneWorkerActions.INITIALIZE:
        initialize(payload);
        break;
      case SceneWorkerActions.CALCULATE_POINT_CLOUD:
        pointCloudResult = calculatePointCloud(
          payload.data,
          payload.tempHeat,
          payload.agreedHeatData,
        );
        self.postMessage({
          type: SceneWorkerActions.POINT_CLOUD_RESULT,
          payload: pointCloudResult,
        });
        break;

      case SceneWorkerActions.CALCULATE_IR_SHELL:
        irShellResult = calculateIRShellData(payload.processedThermalImages);
        self.postMessage({
          type: SceneWorkerActions.IR_SHELL_RESULT,
          payload: irShellResult,
        });
        break;

      case SceneWorkerActions.CALCULATE_DELAUNAY_MESH:
        delaunayResult = calculate3DDelaunayMesh(payload.heatData);
        self.postMessage({
          type: SceneWorkerActions.DELAUNAY_RESULT,
          payload: delaunayResult,
        });
        break;
    }
  };

  const calculatePointCloud = (
    data: string | ArrayBuffer,
    tempHeat: LaserHeatModel,
    agreedHeatData: LidarZoneSelectionModel[],
  ): LaserScanDataModel => {
    const columnCount = tempHeat.columnCount || 8;

    const primitiveArray = (data as string)
      .replace(/\r\n$/, "")
      .split("\r\n")
      .join(",")
      .split(",")
      .reduce((acc: number[], val) => {
        acc.push(val === "???" ? 0 : parseFloat(val));
        return acc;
      }, []);

    const dataLength = primitiveArray.length;
    for (let i = columnCount; i < dataLength; i += columnCount) {
      primitiveArray[i + 4] =
        workerConfig.quadrantOrder[primitiveArray[i + 4] - 1];
    }

    // Ek noktalar için önceden hesaplanmış değerler
    const extraPointsData = [];
    const PI = Math.PI;
    const PI_6 = PI / 6;
    const PI_12 = PI / 12;
    const TO_RADIANS = PI / 180;

    // Ek noktaların daha verimli hesaplanması
    for (let cr = 6.57; cr <= 7; cr += 0.05) {
      const zVal = cr - 6.7;
      for (let ti = 0.3; ti <= 360.3; ti += 0.3) {
        const angle = ti * TO_RADIANS;
        const radius = cr;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;

        let quadrant = Math.floor((Math.atan2(y, x) + PI_12) / PI_6);
        if (quadrant === 6) quadrant = -6;

        const offset = 0.1 * (ti % 2 === 0 ? 1 : -1);

        extraPointsData.push(
          x + offset,
          y + offset,
          zVal,
          9,
          quadrant,
          41,
          4,
          9,
        );
      }
    }

    const startIndex = primitiveArray.length;
    primitiveArray.length += extraPointsData.length;
    for (let i = 0; i < extraPointsData.length; i++) {
      primitiveArray[startIndex + i] = extraPointsData[i];
    }

    // Geometry ve arrays oluşturma optimizasyonu
    const totalPoints = Math.floor(primitiveArray.length / columnCount) - 1;

    // Arrays önceden boyutlandırma
    const heatArray = new Float32Array(totalPoints * 3);
    const heatArrayDefault = new Float32Array(totalPoints * 3);
    const heatColors = new Float32Array(totalPoints * 3);
    const heatMinColors = new Float32Array(totalPoints * 3);
    const heatOriginalColors = new Float32Array(totalPoints * 3);
    const animationDiffs = new Float32Array(totalPoints * 3);
    const pointQuadrantMap = new Int32Array(totalPoints);
    const pointRowMap = new Int32Array(totalPoints);
    const pointMainZoneMap = new Int32Array(totalPoints);
    const pointThicknessMap = new Float32Array(totalPoints);
    const courseThicknessMinMap = new Float32Array(totalPoints);

    // Clock based data structures
    const clockBasedBarrel: LaserScanClockCourseIndexesType = {};
    const clockBasedBarrelIndexes: LaserScanClockCourseIndexesType = {};
    const quarterlyMeshData: QuarterlyMeshDataType = {
      0: [],
      1: [],
      2: [],
      3: [],
    };

    // Main data processing loop optimization
    let arrayIndex = 0;
    for (let i = 1; i < totalPoints; i++) {
      const baseIndex = i * columnCount;
      const x = primitiveArray[baseIndex];
      const y = primitiveArray[baseIndex + 1];
      const z = primitiveArray[baseIndex + 2];
      const depthVal = primitiveArray[baseIndex + 3];
      const quadrantLocIndexN = primitiveArray[baseIndex + 4];
      const rowLocIndexN = primitiveArray[baseIndex + 5];
      const mzoI = primitiveArray[baseIndex + 6] === 0 ? 0 : 1;

      // Position suffix calculation
      const suffixData = workerConfig.positionSuffixMap[quadrantLocIndexN + 6];
      const xSuffix = suffixData.x;
      const ySuffix = suffixData.y;
      const zSuffix = (rowLocIndexN - 41) * 0.25;

      // Array updates - batch processing
      const positionIndex = arrayIndex * 3;
      heatArray[positionIndex] = x;
      heatArray[positionIndex + 1] = y;
      heatArray[positionIndex + 2] = z;

      heatArrayDefault[positionIndex] = x;
      heatArrayDefault[positionIndex + 1] = y;
      heatArrayDefault[positionIndex + 2] = z;

      animationDiffs[positionIndex] = xSuffix;
      animationDiffs[positionIndex + 1] = ySuffix;
      animationDiffs[positionIndex + 2] = zSuffix;

      // Clock based data updates
      if (depthVal !== 0) {
        if (!clockBasedBarrel[mzoI]) {
          clockBasedBarrel[mzoI] = {};
          clockBasedBarrelIndexes[mzoI] = {};
        }

        const rowIndex = rowLocIndexN - 1;
        const quadrantIndex = quadrantLocIndexN.toString();

        if (!clockBasedBarrel[mzoI][rowIndex]) {
          clockBasedBarrel[mzoI][rowIndex] = {};
          clockBasedBarrelIndexes[mzoI][rowIndex] = {};
        }

        if (!clockBasedBarrel[mzoI][rowIndex][quadrantIndex]) {
          clockBasedBarrel[mzoI][rowIndex][quadrantIndex] = [];
          clockBasedBarrelIndexes[mzoI][rowIndex][quadrantIndex] = [];
        }

        clockBasedBarrel[mzoI][rowIndex][quadrantIndex].push(depthVal);
        clockBasedBarrelIndexes[mzoI][rowIndex][quadrantIndex].push(arrayIndex);
      }
      // Color calculations
      const color = new Color(
        assignColorForThickness(convertInchToUserPreference(depthVal), mzoI),
      );

      const colorIndex = arrayIndex * 3;
      heatColors[colorIndex] = color.r;
      heatColors[colorIndex + 1] = color.g;
      heatColors[colorIndex + 2] = color.b;

      // Map updates
      pointQuadrantMap[arrayIndex] = quadrantLocIndexN;
      pointRowMap[arrayIndex] = rowLocIndexN - 1;
      pointMainZoneMap[arrayIndex] = primitiveArray[baseIndex + 6];
      pointThicknessMap[arrayIndex] = depthVal;

      if (quadrantLocIndexN === -5) {
        quarterlyMeshData[0].push(i);
      }

      arrayIndex++;
    }
    const geometry = new BufferGeometry();

    // Geometry attribute updates - batch processing
    geometry.setAttribute("position", new BufferAttribute(heatArray, 3));
    geometry.setAttribute("color", new BufferAttribute(heatColors, 3));
    geometry.setAttribute(
      "plateMinColors",
      new BufferAttribute(heatMinColors, 3),
    );
    geometry.setAttribute(
      "originalColors",
      new BufferAttribute(heatOriginalColors, 3),
    );
    geometry.setAttribute(
      "animationDiffs",
      new BufferAttribute(animationDiffs, 3),
    );
    geometry.setAttribute(
      "heatArrayDefault",
      new BufferAttribute(heatArrayDefault, 3),
    );
    geometry.setAttribute(
      "pointQuadrantMap",
      new BufferAttribute(new Float32Array(pointQuadrantMap), 1),
    );
    geometry.setAttribute(
      "pointMainZoneMap",
      new BufferAttribute(new Float32Array(pointMainZoneMap), 1),
    );
    geometry.setAttribute(
      "pointRowMap",
      new BufferAttribute(new Float32Array(pointRowMap), 1),
    );
    geometry.setAttribute(
      "pointThicknessMap",
      new BufferAttribute(pointThicknessMap, 1),
    );
    geometry.setAttribute(
      "courseThicknessMinMap",
      new BufferAttribute(courseThicknessMinMap, 1),
    );

    return {
      primitiveArray,

      geometry: geometry as never, //INFO, Cant use thereJS original BufferGeometry here
      clockBasedBarrelIndexes,
      quarterlyMeshData,
      courseThicknessMinMap: Array.from(courseThicknessMinMap),
      heatData: agreedHeatData,
    };
  };

  const assignColorForThickness = (thickness: number, mainZone?: number) => {
    if (mainZone == undefined) {
      return "#000000";
    }

    if (mainZone > 0) {
      const scaleVal = workerConfig.sidewallColorsScale.find(
        (scale) =>
          convertInchToUserPreference(scale.min) <= thickness &&
          convertInchToUserPreference(scale.max) > thickness,
      );

      const isAboveScaleVal =
        convertInchToUserPreference(
          workerConfig.sidewallColorsScale[
            workerConfig.sidewallColorsScale.length - 1
          ].max,
        ) <= thickness;

      return scaleVal && scaleVal.color
        ? scaleVal.color
        : isAboveScaleVal
          ? workerConfig.sidewallColorsScale[
              workerConfig.sidewallColorsScale.length - 1
            ].color
          : workerConfig.sidewallColorsScale[0].color;
    } else {
      const scaleVal = workerConfig.bottomColorsScale.find(
        (scale) =>
          convertInchToUserPreference(scale.min) <= thickness &&
          convertInchToUserPreference(scale.max) > thickness,
      );

      const isAboveScaleVal =
        convertInchToUserPreference(
          workerConfig.sidewallColorsScale[
            workerConfig.sidewallColorsScale.length - 1
          ].max,
        ) <= thickness;

      return scaleVal && scaleVal.color
        ? scaleVal.color
        : isAboveScaleVal
          ? workerConfig.sidewallColorsScale[
              workerConfig.sidewallColorsScale.length - 1
            ].color
          : workerConfig.sidewallColorsScale[0].color;
    }
  };
  const getColorForThermalValue = (
    thermalValue: number,
    appliedFactor: number = 1,
  ): string => {
    const minValue = workerConfig.infraredMinValue;
    const maxValue = workerConfig.infraredMaxValue;

    const limitValue =
      thermalValue < workerConfig.infraredMinValue
        ? workerConfig.infraredMinValue
        : thermalValue > workerConfig.infraredMaxValue
          ? workerConfig.infraredMaxValue
          : thermalValue;

    const normalizedValue =
      (limitValue * appliedFactor - minValue) / (maxValue - minValue);

    const colorIndex = Math.floor(
      normalizedValue * (workerConfig.colorMap.length - 1),
    );

    return workerConfig.colorMap[colorIndex] as string;
  };

  const calculateIRShellData = (
    processedThermalImages: CurrentProcessedThermalImageDataModel,
  ) => {
    const mainIRDataSource: Record<string, IRClockCourseData> = {};
    const mainData = processedThermalImages;

    // Sık kullanılan değerleri önbelleğe
    const PI = Math.PI;
    const PI_2 = PI / 2;
    const PI_6 = PI / 6;
    const PI_12 = (PI * 11) / 12;

    const colorCache = new Map<number, InstanceType<typeof WorkerColor>>();

    const remainingClocks = new Set([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);

    for (const key in mainData) {
      const value = mainData[key as keyof typeof mainData];
      if (!value?.ZoneBoundaryInfo?.VerticalLines) continue;

      const { Height: height, HorizontalLines } = value.ZoneBoundaryInfo;
      const slagVal = HorizontalLines.find(
        (zone) => zone.ZoneName === "Slag Line",
      );
      const slagStart = slagVal ? slagVal.YStart : 0;
      const effectiveHeight = height - slagStart;

      // Dikey çizgileri işle
      for (const vLineKey in value.ZoneBoundaryInfo.VerticalLines) {
        const iValue = value.ZoneBoundaryInfo.VerticalLines[vLineKey];
        const clock = iValue.Clock;

        if (mainIRDataSource[clock]) continue;

        const width = iValue.XEnd - iValue.XStart;
        const dataLength = width * effectiveHeight;
        const data = new Array(dataLength);

        // Tek seferde veri kopyalama
        let dataIndex = 0;
        for (let rn = slagStart; rn < height; rn++) {
          const row = value.OutlinedImageData[rn];
          for (let colN = iValue.XStart; colN < iValue.XEnd; colN++) {
            data[dataIndex++] = row[colN];
          }
        }

        mainIRDataSource[clock] = {
          width,
          height: effectiveHeight,
          data,
          calibrationFactor: value.thermalCalibration.factor,
        };

        remainingClocks.delete(clock);
      }
    }

    // Eksik clock'lar için varsayılan değerler
    for (const clock of remainingClocks) {
      mainIRDataSource[clock] = {
        height: 20,
        width: 10,
        data: [],
        calibrationFactor: 1,
      };
    }

    // Mesh verilerini oluştur
    const result = Object.entries(mainIRDataSource).map(([key, value]) => {
      const {
        width: plateWidth,
        height: plateHeight,
        data: imgData,
        calibrationFactor,
      } = value;
      const totalPixels = plateWidth * plateHeight;
      const bytesPerPixel = 4;
      const dataArr = new Uint8Array(totalPixels * bytesPerPixel);

      if (imgData.length > 0) {
        // Toplu renk hesaplama ve buffer doldurma
        for (let qn = 0; qn < imgData.length; qn++) {
          const thermalValue = imgData[qn] * (calibrationFactor || 1);
          const colorInstance =
            colorCache.get(thermalValue) ||
            new WorkerColor(getColorForThermalValue(thermalValue));

          const index = qn * bytesPerPixel;

          dataArr[index] = Math.floor(colorInstance?.r * 255);
          dataArr[index + 1] = Math.floor(colorInstance?.g * 255);
          dataArr[index + 2] = Math.floor(colorInstance?.b * 255);
          dataArr[index + 3] = 255;
        }
      } else {
        // Boş alanları gri renkle doldur
        dataArr.fill(66, 0, totalPixels * bytesPerPixel);
        for (let i = 3; i < totalPixels * bytesPerPixel; i += 4) {
          dataArr[i] = 255;
        }
      }

      const numKey = +key;
      return {
        key,
        meshProps: {
          position: [0, 0, -6.6],
          rotation: [PI_2, PI_12 + numKey * -PI_6, 0],
          name: `irshell-${key}`,
        },
        geometryProps: {
          args: [7.4, 6.2, 13.5, 40, 30, true, 0, PI_6],
        },
        materialProps: {
          side: 2, //THREE.DoubleSide,
          transparent: true,
          opacity: 1,
          map: {
            data: dataArr,
            temperatureData: value.data,
            calibrationFactor,
            width: plateWidth,
            height: plateHeight,
            format: 1023, //RGBAFormat
            flipY: true,
          },
        },
      } as IRShellMeshData;
    });
    return result;
  };

  const calculate3DDelaunayMesh = (heatData: LaserScanDataModel) => {
    const points3dFull: Record<string, number[]> = {};

    // Sidewall points (index 1)
    Object.entries(heatData.clockBasedBarrelIndexes[1] || {}).forEach(
      ([, value]) => {
        Object.entries(value).forEach(([iKey, iValue]) => {
          if (iValue && iValue.length > 0) {
            points3dFull[iKey] = points3dFull[iKey]
              ? [...points3dFull[iKey], ...iValue]
              : [...iValue];
          }
        });
      },
    );

    // Bottom points (index 0)
    Object.entries(heatData.clockBasedBarrelIndexes[0] || {}).forEach(
      ([, value]) => {
        Object.entries(value).forEach(([, iValue]) => {
          if (iValue && iValue.length > 0) {
            points3dFull["bottom"] = points3dFull["bottom"]
              ? [...points3dFull["bottom"], ...iValue]
              : [...iValue];
          }
        });
      },
    );

    const points3dFull2: Record<string, number[]> = {
      bottom: [...(points3dFull["bottom"] || [])],
      north: [
        ...(points3dFull["4"] || []),
        ...(points3dFull["3"] || []),
        ...(points3dFull["2"] || []),
      ],
      east: [
        ...(points3dFull["1"] || []),
        ...(points3dFull["0"] || []),
        ...(points3dFull["-1"] || []),
      ],
      south: [
        ...(points3dFull["-2"] || []),
        ...(points3dFull["-3"] || []),
        ...(points3dFull["-4"] || []),
      ],
      west: [
        ...(points3dFull["5"] || []),
        ...(points3dFull["-6"] || []),
        ...(points3dFull["-5"] || []),
      ],
    };

    return {
      points3dFull2,
      geometryData: {
        position: heatData.geometry.attributes.position.array,
        color: heatData.geometry.attributes.color.array,
        pointThicknessMap: heatData.geometry.attributes.pointThicknessMap.array,
        pointQuadrantMap: heatData.geometry.attributes.pointQuadrantMap.array,
        pointRowMap: heatData.geometry.attributes.pointRowMap.array,
        pointMainZoneMap: heatData.geometry.attributes.pointMainZoneMap.array,
      },
    };
  };
};

export default SceneWorker;
